"use client";

import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { createBrowserClient } from "@/utils/supabase/client";

interface UnseenAchievement {
  key: string;
  title: string;
  emoji: string;
}

/**
 * Mounted once near the root of the app (see components/LayoutClient.tsx).
 * On mount, checks whether there's a logged-in session and — only then —
 * fetches newly-earned-but-unseen achievements and toasts each one, then
 * marks them seen so they don't toast again on the next page load.
 *
 * Deliberately uses `fetch()` directly instead of `apiClient`: this
 * component mounts on every page including fully public ones, and
 * apiClient's response interceptor toasts + redirects on any 401. Even
 * though we gate the fetch behind a client-side session check first, using
 * plain fetch means a stale/expired-session edge case degrades to a
 * silent no-op instead of an unexpected "Please login" toast + redirect on
 * a logged-out visitor browsing a public page.
 */
export default function AchievementToaster(): null {
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async (): Promise<void> => {
      try {
        const supabase = createBrowserClient();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const res = await fetch("/api/achievements?unseen=1", {
          credentials: "include",
        });
        if (!res.ok) return;

        const data = (await res.json()) as { achievements?: UnseenAchievement[] };
        const achievements = data.achievements ?? [];
        if (achievements.length === 0) return;

        for (const achievement of achievements) {
          toast(`${achievement.emoji} Achievement unlocked: ${achievement.title}`);
        }

        await fetch("/api/achievements/seen", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ keys: achievements.map((a) => a.key) }),
        });
      } catch {
        // Achievement toasts are a nice-to-have — never surface an error.
      }
    };

    void run();
  }, []);

  return null;
}
